import { useEffect, useMemo, useState } from 'react';
import { Alert, Box, Button, FormField, Modal, Multiselect, SpaceBetween, Spinner, Textarea } from '@cloudscape-design/components';
import { useNotify, type createApiClient } from '@deloitte-us-consulting/gp-sdk';
import GrantFieldsEditor from './GrantFieldsEditor';
import { systemName } from '../data/systems';
import { needsReason, type DataGrant, type SourceDetail, type TableSummary } from './types';

type Api = ReturnType<typeof createApiClient>;

export interface ReviewRequest {
  id: string;
  appId: string;
  displayName: string;
  /** Whole systems the builder asked for. The review chooses which of their tables to grant. */
  dataSystems?: string[];
  /** Tables named directly (requests from before systems, or added by the platform team). */
  dataSources: string[];
  dataNeed?: string;
  dataReview: { status: 'not_needed' | 'pending' | 'complete'; grants?: DataGrant[]; notes?: string };
}

/** The tables a review may grant for a request: enabled ones belonging to the systems it asked for. */
export const tablesOfSystems = (all: TableSummary[], systems: string[]): TableSummary[] =>
  all.filter((t) => t.enabled && systems.includes(t.connector ?? 'airtable'));

interface Props {
  api: Api;
  request: ReviewRequest | null;
  onDismiss: () => void;
  onSaved: () => void;
}

/**
 * Record the outcome of the data review call for an onboarding request: which tables the
 * app gets, and which fields of each. When the builder asked for a whole system the tables
 * are chosen here, from that system's tables; a table left unchosen, or chosen with no
 * fields, is denied. Approval stays blocked until this is saved.
 */
export default function DataReviewModal({ api, request, onDismiss, onSaved }: Props) {
  const { notify } = useNotify();
  const [offered, setOffered] = useState<TableSummary[] | null>(null);
  const [chosen, setChosen] = useState<string[]>([]);
  const [details, setDetails] = useState<Record<string, SourceDetail>>({});
  const [selected, setSelected] = useState<Record<string, string[]>>({});
  const [reasons, setReasons] = useState<Record<string, string>>({});
  const [notes, setNotes] = useState('');
  const [loadError, setLoadError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const systems = request?.dataSystems ?? [];
  const requestId = request?.id;

  // Opening a different request resets everything. A request that names a system also fetches
  // that system's tables for the picker; a request that names only tables needs no picker.
  useEffect(() => {
    if (!request) return;
    let cancelled = false;
    const granted = request.dataReview.grants ?? [];
    setOffered(null);
    setDetails({});
    setLoadError(null);
    setNotes(request.dataReview.notes ?? '');
    setReasons({});
    setSelected(Object.fromEntries(granted.map((g) => [g.sourceId, g.fieldIds])));
    setChosen([...new Set([...granted.map((g) => g.sourceId), ...request.dataSources])]);
    if ((request.dataSystems ?? []).length > 0) {
      api
        .get<TableSummary[]>('/api/catalog/sources')
        .then((all) => {
          if (!cancelled) setOffered(tablesOfSystems(all, systems));
        })
        .catch((e) => {
          if (!cancelled) setLoadError((e as Error).message);
        });
    }
    return () => {
      cancelled = true;
    };
    // Reset only when a different request is opened.
  }, [requestId, api]);

  // Load the fields of every chosen table that has not been loaded yet.
  const missing = chosen.filter((id) => !details[id]);
  const missingKey = missing.join('|');
  useEffect(() => {
    if (!request || missing.length === 0) return;
    let cancelled = false;
    Promise.all(missing.map((id) => api.get<SourceDetail>(`/api/catalog/sources/${id}`)))
      .then((loaded) => {
        if (!cancelled) setDetails((cur) => ({ ...cur, ...Object.fromEntries(loaded.map((d) => [d.sourceId, d])) }));
      })
      .catch((e) => {
        if (!cancelled) setLoadError((e as Error).message);
      });
    return () => {
      cancelled = true;
    };
  }, [missingKey, api, requestId]);

  const labelFor = useMemo(() => {
    const names = new Map((offered ?? []).map((t) => [t.sourceId, t.displayName]));
    return (id: string) => names.get(id) ?? details[id]?.displayName ?? id;
  }, [offered, details]);

  if (!request) return null;

  const loading = !loadError && (missing.length > 0 || (systems.length > 0 && offered === null));
  const chosenDetails = chosen.map((id) => details[id]).filter((d): d is SourceDetail => !!d);
  const missingReason = chosenDetails.some((s) => needsReason(s, selected[s.sourceId] ?? []) && !(reasons[s.sourceId] ?? '').trim());
  const granted = chosen.filter((id) => (selected[id] ?? []).length > 0);

  const save = async () => {
    setSaving(true);
    try {
      await api.post(`/api/onboarding/${request.id}/data-review`, {
        grants: granted.map((id) => ({
          sourceId: id,
          fieldIds: selected[id],
          ...((reasons[id] ?? '').trim() ? { reason: reasons[id]!.trim() } : {}),
        })),
        notes: notes.trim() || undefined,
      });
      notify({
        type: 'success',
        header: 'Data review recorded',
        content: `${request.appId}: ${granted.length} table${granted.length === 1 ? '' : 's'} granted. You can now approve the app.`,
      });
      onSaved();
    } catch (e) {
      notify({ type: 'error', header: 'Could not record the data review', content: (e as Error).message });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      visible
      size="large"
      onDismiss={onDismiss}
      header={`Data review: ${request.displayName}`}
      footer={
        <Box float="right">
          <SpaceBetween size="xs" direction="horizontal">
            <Button variant="link" onClick={onDismiss}>
              Cancel
            </Button>
            <Button variant="primary" loading={saving} disabled={loading || missingReason} onClick={save}>
              Record outcome
            </Button>
          </SpaceBetween>
        </Box>
      }
    >
      <SpaceBetween size="l">
        <Box color="text-body-secondary">
          {systems.length > 0 ? (
            <>
              <code>{request.appId}</code> asked for {systems.map(systemName).join(', ')}. Agree with the builder which tables and
              which fields of each it needs, then record it here. Anything not chosen is denied.
            </>
          ) : (
            <>
              Agree with the builder which fields of each requested table <code>{request.appId}</code> needs, then record it here.
              Anything left empty is denied.
            </>
          )}{' '}
          The grants are written to the app when you approve it.
        </Box>
        {request.dataNeed && (
          <FormField label="What the builder said they need it for">
            <Box>{request.dataNeed}</Box>
          </FormField>
        )}
        {loadError && <Alert type="error">Could not load the data for this request: {loadError}</Alert>}
        {systems.length > 0 && (
          <FormField
            label="Tables to grant"
            description="Choose the tables the call settled on. Each one opens its fields below. Restricted tables need a stated reason."
          >
            <Multiselect
              selectedOptions={chosen.map((id) => ({ value: id, label: labelFor(id) }))}
              options={(offered ?? []).map((t) => ({
                value: t.sourceId,
                label: t.displayName,
                description: t.summary,
                ...(t.defaultClass === 'restricted' ? { tags: ['Restricted'] } : {}),
              }))}
              onChange={({ detail }) => setChosen(detail.selectedOptions.map((o) => o.value).filter((v): v is string => !!v))}
              placeholder={offered === null ? 'Loading tables…' : 'No tables: deny the request'}
              filteringType="auto"
              statusType={offered === null && !loadError ? 'loading' : 'finished'}
              ariaLabel="Tables to grant"
            />
          </FormField>
        )}
        {loading && <Spinner />}
        {chosenDetails.map((s) => (
          <GrantFieldsEditor
            key={s.sourceId}
            source={s}
            selected={selected[s.sourceId] ?? []}
            onChange={(ids) => setSelected((cur) => ({ ...cur, [s.sourceId]: ids }))}
            reason={reasons[s.sourceId] ?? ''}
            onReasonChange={(r) => setReasons((cur) => ({ ...cur, [s.sourceId]: r }))}
          />
        ))}
        <FormField label="Notes from the call (optional)">
          <Textarea value={notes} onChange={({ detail }) => setNotes(detail.value)} rows={2} ariaLabel="Notes from the call" />
        </FormField>
      </SpaceBetween>
    </Modal>
  );
}
